import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronLeft, Mail, Lock, Trash2, Camera, User, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FloatingInput } from '@/components/ui/floating-input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { signOut } from 'firebase/auth'
import { auth } from '@/constants'

// Email update schema
const emailSchema = z.object({
	email: z.string().email('Please enter a valid email address')
})

// Password update schema
const passwordSchema = z.object({
	currentPassword: z.string().min(1, 'Current password is required'),
	newPassword: z.string().min(8, 'Password must be at least 8 characters'),
	confirmPassword: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.newPassword === data.confirmPassword, {
	message: "Passwords don't match",
	path: ['confirmPassword']
})

type EmailFormData = z.infer<typeof emailSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export default function AccountPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid, email, displayName, disconnectWallet, isConnected, setUserData } = useUser()

	const [isUpdatingEmail, setIsUpdatingEmail] = useState(false)
	const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [deletePassword, setDeletePassword] = useState('')
	const [deletePasswordError, setDeletePasswordError] = useState('')
	const [profileImage, setProfileImage] = useState<string | null>(null)
	const [isUploadingImage, setIsUploadingImage] = useState(false)

	// Email form
	const emailForm = useForm<EmailFormData>({
		resolver: zodResolver(emailSchema),
		defaultValues: {
			email: email || ''
		}
	})

	// Password form
	const passwordForm = useForm<PasswordFormData>({
		resolver: zodResolver(passwordSchema),
		defaultValues: {
			currentPassword: '',
			newPassword: '',
			confirmPassword: ''
		}
	})

	// Handle email update
	const onEmailSubmit = async (data: EmailFormData) => {
		if (data.email === email) {
			toast({
				title: 'No Changes',
				description: 'The email address is the same as your current one.',
				variant: 'destructive'
			})
			return
		}

		setIsUpdatingEmail(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}/email`, {
				method: 'PATCH',
				body: JSON.stringify({ email: data.email }),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update email')
			}

			toast({
				title: 'Email Updated',
				description: 'A verification email has been sent to your new address.'
			})
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update email',
				variant: 'destructive'
			})
		} finally {
			setIsUpdatingEmail(false)
		}
	}

	// Handle password update
	const onPasswordSubmit = async (data: PasswordFormData) => {
		setIsUpdatingPassword(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}/password`, {
				method: 'PATCH',
				body: JSON.stringify({
					currentPassword: data.currentPassword,
					newPassword: data.newPassword
				}),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update password')
			}

			toast({
				title: 'Password Updated',
				description: 'Your password has been successfully changed.'
			})
			passwordForm.reset()
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update password',
				variant: 'destructive'
			})
		} finally {
			setIsUpdatingPassword(false)
		}
	}

	// Handle profile image upload
	const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		// Validate file type
		if (!file.type.startsWith('image/')) {
			toast({
				title: 'Invalid File',
				description: 'Please select an image file.',
				variant: 'destructive'
			})
			return
		}

		// Validate file size (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: 'File Too Large',
				description: 'Please select an image smaller than 5MB.',
				variant: 'destructive'
			})
			return
		}

		setIsUploadingImage(true)
		try {
			const formData = new FormData()
			formData.append('image', file)

			const response = await apiFetch(`/v1/users/${uid}/profile-image`, {
				method: 'POST',
				body: formData,
				withCredentials: true,
				headers: {} // Let browser set Content-Type for FormData
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to upload image')
			}

			setProfileImage(result.data?.imageUrl || URL.createObjectURL(file))
			toast({
				title: 'Image Uploaded',
				description: 'Your profile image has been updated.'
			})
		} catch (error) {
			toast({
				title: 'Upload Failed',
				description: error instanceof Error ? error.message : 'Failed to upload image',
				variant: 'destructive'
			})
		} finally {
			setIsUploadingImage(false)
		}
	}

	// Handle account deletion
	const handleDeleteAccount = async () => {
		// Validate password is entered
		if (!deletePassword.trim()) {
			setDeletePasswordError('Please enter your password to confirm')
			return
		}

		setDeletePasswordError('')
		setIsDeleting(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'DELETE',
				body: JSON.stringify({ password: deletePassword }),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to delete account')
			}

			// Clear session and sign out
			document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
			await signOut(auth)
			setUserData(null)

			if (isConnected) {
				await disconnectWallet()
			}

			toast({
				title: 'Account Deleted',
				description: 'Your account has been successfully deleted.'
			})

			navigate('/login')
		} catch (error) {
			toast({
				title: 'Deletion Failed',
				description: error instanceof Error ? error.message : 'Failed to delete account',
				variant: 'destructive'
			})
		} finally {
			setIsDeleting(false)
			setShowDeleteModal(false)
			setDeletePassword('')
		}
	}

	// Handle closing delete modal
	const handleCloseDeleteModal = () => {
		setShowDeleteModal(false)
		setDeletePassword('')
		setDeletePasswordError('')
	}

	// Get user initials for avatar
	const userInitials = displayName
		? displayName
				.split(' ')
				.map((name: string) => name[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: email?.slice(0, 2).toUpperCase() || 'U'

	return (
		<div className="min-h-screen bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Header */}
				<div
					role="banner"
					className="h-12 flex items-center justify-center text-white mb-6 pt-3 relative"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="absolute left-0 h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>

					<h1 className="text-base font-semibold tracking-wide">Account Settings</h1>
				</div>

				{/* Profile Image Section */}
				<Card className="p-6 bg-[#3C3C3C] border-0 mb-4">
					<div className="flex items-center gap-4">
						<div className="relative">
							{profileImage ? (
								<img
									src={profileImage}
									alt="Profile"
									className="h-20 w-20 rounded-full object-cover border-2 border-[#4C4C4C]"
								/>
							) : (
								<div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#d85b5b] to-[#8e2b2b] flex items-center justify-center text-white font-semibold text-2xl border-2 border-[#4C4C4C]">
									{userInitials}
								</div>
							)}
							<label
								htmlFor="profile-image-input"
								className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-[#EF4444] hover:bg-[#DC2626] flex items-center justify-center cursor-pointer transition-colors"
							>
								{isUploadingImage ? (
									<Loader2 className="h-4 w-4 text-white animate-spin" />
								) : (
									<Camera className="h-4 w-4 text-white" />
								)}
							</label>
							<input
								id="profile-image-input"
								type="file"
								accept="image/*"
								className="hidden"
								onChange={handleImageUpload}
								disabled={isUploadingImage}
							/>
						</div>
						<div>
							<h2 className="text-lg font-semibold text-[#ffffff]">{displayName || 'User'}</h2>
							<p className="text-sm text-[#BABABB]">{email}</p>
						</div>
					</div>
				</Card>

				{/* Email Section */}
				<Card className="p-6 bg-[#3C3C3C] border-0 mb-4">
					<div className="flex items-center gap-2 mb-4">
						<Mail className="h-5 w-5 text-[#EF4444]" />
						<h3 className="text-base font-semibold text-[#ffffff]">Email Address</h3>
					</div>

					<Form {...emailForm}>
						<form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
							<FormField
								control={emailForm.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												type="email"
												label="Email"
												autoComplete="email"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={isUpdatingEmail}
								className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
							>
								{isUpdatingEmail ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Updating...
									</>
								) : (
									'Update Email'
								)}
							</Button>
						</form>
					</Form>
				</Card>

				{/* Password Section */}
				<Card className="p-6 bg-[#3C3C3C] border-0 mb-4">
					<div className="flex items-center gap-2 mb-4">
						<Lock className="h-5 w-5 text-[#EF4444]" />
						<h3 className="text-base font-semibold text-[#ffffff]">Change Password</h3>
					</div>

					<Form {...passwordForm}>
						<form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
							<FormField
								control={passwordForm.control}
								name="currentPassword"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												type="password"
												label="Current Password"
												autoComplete="current-password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={passwordForm.control}
								name="newPassword"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												type="password"
												label="New Password"
												autoComplete="new-password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={passwordForm.control}
								name="confirmPassword"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<FloatingInput
												{...field}
												type="password"
												label="Confirm New Password"
												autoComplete="new-password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={isUpdatingPassword}
								className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
							>
								{isUpdatingPassword ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Updating...
									</>
								) : (
									'Change Password'
								)}
							</Button>
						</form>
					</Form>
				</Card>

				{/* Delete Account Section */}
				<Card className="p-6 bg-[#3C3C3C] border-0">
					<div className="flex items-center gap-2 mb-4">
						<Trash2 className="h-5 w-5 text-[#EF4444]" />
						<h3 className="text-base font-semibold text-[#ffffff]">Delete Account</h3>
					</div>

					<p className="text-sm text-[#BABABB] mb-4">
						Once you delete your account, there is no going back. Your account will be deactivated
						and you can re-register with the same email address later.
					</p>

					<Button
						variant="destructive"
						onClick={() => setShowDeleteModal(true)}
						className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white"
					>
						Delete Account
					</Button>
				</Card>
			</div>

			{/* Delete Confirmation Modal with Password */}
			<AlertDialog open={showDeleteModal} onOpenChange={handleCloseDeleteModal}>
				<AlertDialogContent className="rounded-2xl shadow-lg border border-gray-200 text-center p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2">
						<AlertDialogTitle className="text-lg sm:text-xl font-semibold text-white">
							Delete Account
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB]">
							This action cannot be undone. Your account will be deactivated and your data will be removed.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="my-4">
						<FloatingInput
							id="delete-password"
							type="password"
							value={deletePassword}
							onChange={(e) => {
								setDeletePassword(e.target.value)
								setDeletePasswordError('')
							}}
							label="Enter your password to confirm"
							disabled={isDeleting}
						/>
						{deletePasswordError && (
							<p className="text-sm text-red-500 text-left mt-1">{deletePasswordError}</p>
						)}
					</div>

					<AlertDialogFooter className="flex flex-col sm:flex-row justify-between gap-3">
						<AlertDialogCancel
							className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-5 py-2 rounded-lg transition-colors"
							disabled={isDeleting}
						>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								handleDeleteAccount()
							}}
							disabled={isDeleting}
							className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white font-medium px-5 py-2 rounded-lg transition-colors"
						>
							{isDeleting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Deleting...
								</>
							) : (
								'Delete Account'
							)}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
